import React from "react";
import DataGrid from '@dataGrid';
import GridConfig from "./Grid/GridConfig";
import { withUserProfile } from "../../../../../../../userProfile/userProfileContext";
import { compose } from "recompose";
import { inject } from "mobx-react";
import { withSnackbar } from "notistack";
import styles from "./styles";
import { withStyles } from "@material-ui/core";
import FooterDetails from "./Grid/components/FooterDetails";
import GridError from "./Grid/components/GridError";
import {
  getShipmentErrorInfo,
  getDefaultShipment,
  getDefaultShipmentError,
  isEmptyShipmentObj,
} from "./utils";
import Refresh from '@material-ui/icons/Refresh';
import appConstants from "../../../../../../../appConstants";
import StyledTooltip from "../../../../../../common/StyledToolTip";
import { autorun } from "mobx";
import { isEqual } from "lodash/lang";

const didShipmentGridDisplayContextChange = (prevProps, nextProps) =>
  prevProps.mode !== nextProps.mode ||
  prevProps.isReadOnlyPickupMergeMode !== nextProps.isReadOnlyPickupMergeMode ||
  prevProps.isBulkEditPickup !== nextProps.isBulkEditPickup;

class ShipmentInfoForm extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig(
      this.props.userData.userProfile,
      this.props.title,
      this.props.mode
    );
    // Service Level cells store the mapped value, so the grid needs the options to show the label
    this.serviceLevelOptions = this.props.DispatchStore.serviceLevelShipmentOptions;
    this.gridContext = {
      serviceLevelOptions: this.serviceLevelOptions
    };
    this.gridApi = {};
  }

  componentDidMount() {
    // Keep service level labels in sync if client codes arrive after this grid is already open (same approach as the Pickups and Drivers grids)
    this.autorunServiceLevelOptions = autorun(() => {
      const serviceLevelOptions = this.props.DispatchStore.serviceLevelShipmentOptions;
      if (!isEqual(this.serviceLevelOptions, serviceLevelOptions)) {
        this.serviceLevelOptions = serviceLevelOptions;
        this.gridContext.serviceLevelOptions = serviceLevelOptions;
        this.gridApi?.redrawRows?.();
      }
    });
  }

  componentWillUnmount() {
    this.autorunServiceLevelOptions?.();
  }

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.gridApi.mode = this.props.mode;
    this.gridApi.isDataUpdatedOnGrid = false;
    this.syncEmbargoShipmentRowIds();
    this.gridApi.redrawRows();
    this.props.onGridReady(api);
    this.syncShipmentGridModeFlags();
  };

  syncEmbargoShipmentRowIds = () => {
    if (!this.gridApi?.refreshCells) {
      return;
    }
    this.gridApi.embargoShipmentErrorRowIds = (this.props.embargoShipmentViolations || [])
      .map((violation) => violation.shipmentRowId)
      .filter(Boolean);
    this.gridApi.embargoShipmentWarningRowIds = (this.props.embargoShipmentWarnings || [])
      .map((warning) => warning.shipmentRowId)
      .filter(Boolean);
    this.gridApi.refreshCells({ force: true });
  };

  syncShipmentGridModeFlags = () => {
    if (!this.gridApi || !this.gridApi.mode) {
      return;
    }
    this.gridApi.mode = this.props.mode;
    this.gridApi.isReadOnlyPickupMergeMode =
      this.props.isReadOnlyPickupMergeMode || Boolean(this.props.isBulkEditPickup);
  };

  componentDidUpdate(prevProps) {
    if (!this.gridApi.isDataUpdatedOnGrid) {
      this.gridApi.isDataUpdatedOnGrid = true;
    }
    if (didShipmentGridDisplayContextChange(prevProps, this.props)) {
      this.syncShipmentGridModeFlags();
      this.gridApi?.redrawRows?.();
    }
    if (
      prevProps.embargoShipmentViolations !== this.props.embargoShipmentViolations ||
      prevProps.embargoShipmentWarnings !== this.props.embargoShipmentWarnings
    ) {
      this.syncEmbargoShipmentRowIds();
    }
  }

  getAllRows() {
    let rowData = [];
    this.gridApi.forEachNode((node) => rowData.push(node.data));
    return rowData;
  }

  onAddRow = () => {
    const { shipments, shipmentsErrors } = this.props.shipmentInfo;
    const newShipments= [...shipments, getDefaultShipment()];
    const newShipmentsErrors= [...shipmentsErrors, getDefaultShipmentError()];
    if (shipments.length !== 0 && isEmptyShipmentObj(shipments.at(-1)));
    else {
      this.props.handleChange(
        "shipmentInfo",
        newShipments,
        newShipmentsErrors
      );
    }
    this.enableCellForEditing();
  };

  updateShipmentInfo = () => {
    const shipments = this.getAllRows();
    const newShipmentsErrors = getShipmentErrorInfo(shipments, this.props.mode);
    this.props.handleChange(
      "shipmentInfo",
      shipments,
      newShipmentsErrors,
    );
  };

  onRemoveRow = () => {
    const selectedRows = this.gridApi.getSelectedRows();
    this.gridApi.applyTransaction({
      remove: selectedRows
    });
    this.updateShipmentInfo();
  };

  updateLocationInGrid = (params) => {
    const data = params.newValue;
    params.data["destinationZip"] = data.zip || "";
    params.data["destinationState"] = data?.state || "";
    params.data["destinationCity"] = data?.city || "";
    params.data["destinationTerminal"] = data?.terminalCode || "";
    params.data["isZipUpdatedManually"] = true;
    this.gridApi.refreshCells({
      rowNodes: [params.node],
      columns: [
        "destinationZip",
        "destinationState",
        "destinationCity",
        "destinationTerminal",
      ],
      force: true,
    });
    this.updateShipmentInfo();
  };

  onCellValueChanged = (params) => {
    const { field } = params.colDef;
    if (field === "destinationZip") {
      if (params.oldValue && !params.newValue) {
        return;
      } else {
        this.updateLocationInGrid(params);
        return;
      }
    }
    this.gridApi.refreshCells({ columns: [params.colDef], force: true });
    this.updateShipmentInfo();
  };

  enableCellForEditing = () => {
    setTimeout(() => {
      this.gridApi.deselectAll(); // Deselect all rows first
      this.gridApi.forEachNode(({ data, rowIndex }) => {
        if (isEmptyShipmentObj(data)) {
          this.gridApi.setFocusedCell(rowIndex, "handlingUnits");
          this.gridApi.startEditingCell({
            rowIndex: rowIndex,
            colKey: "handlingUnits",
          });
        }
      });
    },100);
  };

  onCellEditingStarted = (params) => {
    params.node.setSelected(true);
  };

  onTabToNextCell = ({nextCellPosition}) => {
    if (nextCellPosition) {
      return nextCellPosition;
    }
    const { shipments } = this.props.shipmentInfo;
    if (!nextCellPosition && !isEmptyShipmentObj(shipments.at(-1))) {
      this.onAddRow();
    }
    return false;
  }

  suppressTabKeyInZipDropdown = (params) => {
    const KEY_TAB = 9;
    const { keyCode } = params.event || {};
    if (params.column.colId === "destinationZip") {
      return keyCode === KEY_TAB && this.gridApi.suppressTab; 
    }
    return false;
  }

  getShipmentRowId = (params) => {
    return params.data.pickupShipmentRowId;
  }

  render() {
    const {
      shipmentInfo: { shipments, shipmentsErrors },
      classes,
      mode,
      shipmentGridError,
      getPickupShipments,
      isReadOnlyPickupMergeMode,
      isBulkEditPickup
    } = this.props;
    const data = shipments.length > 0 ? shipments.slice() : [];
    return (
      <div className={`${classes.container} customScroll`}>
        <div className={classes.headerStyle}>
          <span className={classes.headerLabel}>Shipment Info</span>
          {shipmentGridError === appConstants.GRID_STATUS.ERROR && (
              <StyledTooltip title="Retry">
                <Refresh onClick={getPickupShipments} className={classes.refreshIconStyle} />
              </StyledTooltip>
            )}
        </div>
        <div className={classes.dataGrid}>
          <DataGrid
            defaultColDef={this.gridConfig.config.defaultColDef}
            columnDefs={this.gridConfig.config.columns}
            columnTypes={this.gridConfig.config.columnTypes}
            config={this.gridConfig.config}
            rowSelection={this.gridConfig.config.rowSelection}
            rowDeselection={this.gridConfig.config.rowDeselection}
            rowData={data}
            context={this.gridContext}
            onGridReady={this.onGridReady}
            onCellDoubleClicked={this.onDoubleClicked}
            isExternalFilterPresent={() => false}
            onCellValueChanged={this.onCellValueChanged}
            onCellEditingStarted={this.onCellEditingStarted}
            tabToNextCell={this.onTabToNextCell}
            suppressKeyboardEvent={this.suppressTabKeyInZipDropdown}
            showErrorMessage={shipmentGridError}
            getRowId={this.getShipmentRowId}
          />
        </div>
        <FooterDetails
          data={data}
          mode={mode}
          disableRemoveRow={isReadOnlyPickupMergeMode && !isBulkEditPickup}
          disableAddRow={isReadOnlyPickupMergeMode || isBulkEditPickup}
          hideAddRow={isBulkEditPickup}
          onAddRow={this.onAddRow}
          onRemoveRow={this.onRemoveRow}
        />
        <GridError shipmentsErrors={shipmentsErrors} />
      </div>
    );
  }
}
export default compose(
  withUserProfile,
  withSnackbar,
  withStyles(styles),
  inject("DispatchStore")
)(ShipmentInfoForm);
